import { Marker } from '@react-google-maps/api';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { Icons } from 'src/assets/fonts/Icons/icons';
import { localeStore } from 'src/mobx/localesStore';
import { modalStore } from 'src/mobx/modalStore';
import client from 'src/services/client';
import { LatLng } from 'src/types/main';
import { formatImagePath, truncateLongString } from 'src/utils/formatter';
import { formatDate, getTranslate } from 'src/utils/helpers';
import CopyButton from 'src/view/components/Controls/CopyButton/CopyButton';
import CustomModalComponent from 'src/view/components/Controls/Modal/Modal';
import ModalBackground from 'src/view/components/Controls/Modal/ModalBackground/ModalBackground';
import ModalHeader from 'src/view/components/Controls/Modal/ModalHeader/ModalHeader';
import TimeLine from 'src/view/components/Controls/TimeLine/TimeLine';
import MapNotification from 'src/view/components/Map/MapNotification/MapNotification';
import ScrollableBlock from 'src/view/containers/ScrollableBlock/ScrollableBlock';
import DueTime from 'src/view/screens/DeliveryManagementScreen/screens/DeliveryTrackingScreen/components/DueTime/DueTime';
import {
  DELIVERY_REQUEST_STATUS,
  mapIcons,
} from 'src/view/screens/DeliveryManagementScreen/screens/DeliveryTrackingScreen/constants/deliveryTracking';
// eslint-disable-next-line max-len
import DeliveryTrackingMapContainer from 'src/view/screens/DeliveryManagementScreen/screens/DeliveryTrackingScreen/containers/DeliveryTrackingMapContainer/DeliveryTrackingMapContainer';
import {
  addTimeZoneToRequest,
  getDriverInfo,
  getStatusColor,
  getTimerDueTime,
  getTimerStopValue,
  isNotFullTimeLine,
} from 'src/view/screens/DeliveryManagementScreen/screens/DeliveryTrackingScreen/helpers/deliveryTracking';
import { deliveryTrackingStore } from 'src/view/screens/DeliveryManagementScreen/screens/DeliveryTrackingScreen/store/deliveryTrackingStore';
import {
  IDeliveryInfo,
  ITakerGoInfo,
  TTakerGoDetails,
} from 'src/view/screens/DeliveryManagementScreen/screens/DeliveryTrackingScreen/types/deliveryTracking';
import logger from '~/services/logger';
import Label from '~/view/components/Controls/Label';
import './style.scss';

const DeliveryTrackingModal = observer((): JSX.Element => {
  const [deliveryInfo, setDeliveryInfo] = useState<IDeliveryInfo | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);

  const branchLocation: LatLng = modalStore.deliveryTrackingModal!.branchLocation;
  const customerLocation: LatLng = modalStore.deliveryTrackingModal!.customerLocation;

  useEffect(() => {
    fetchDeliveryInfo(modalStore.deliveryTrackingModal?.orderId);
  }, []);

  const fetchDeliveryInfo = async (orderId?: string | number) => {
    try {
      setLoading(true);
      const response = await client.get(`v3/orders/${String(orderId)}/details/delivery`);
      setDeliveryInfo(addTimeZoneToRequest({ takergo: response.takergo, takergo_details: response.takergo_details }));
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      logger.error({ error, place: 'DeliveryTrackingModal fetchDeliveryInfo' });
    }
  };

  const renderTimeLineTrailing = (event: TTakerGoDetails) => {
    return (
      <>
        <div className="d-flex flex-row justify-content-between align-items-center">
          <Label text={event.dsp_status} />
          <p className="family-regular text-size-14 px-3">{formatDate(event.created_at_utc, {})}</p>
        </div>
        {event.taker_go_status === DELIVERY_REQUEST_STATUS.ASSIGNED ? (
          <div className="d-flex delivery-tracking-modal__content-driver-info">
            <p className="family-bold letter-spacing-12 text-uppercase text-size-10 color-gray2">{getDriverInfo(event)}</p>
          </div>
        ) : null}
      </>
    );
  };

  const renderDriverLocation = (takergo: ITakerGoInfo) => {
    const { driver_location } = takergo;

    if (!driver_location.is_tracking_supported) {
      return <MapNotification text={localeStore.t('tracking_not_available_notification')} />;
    }

    return driver_location.lat && driver_location.lng ? (
      <Marker
        label={{ className: 'pulse-label', text: ' ' }}
        position={{ lat: driver_location.lat, lng: driver_location.lng }}
        icon={{ url: formatImagePath(`${Icons.Car}.svg`), scaledSize: new google.maps.Size(14, 14) }}
      />
    ) : null;
  };

  const onMapLoad = () => {
    deliveryTrackingStore.setCamera([
      [branchLocation.lat, branchLocation.lng],
      [customerLocation.lat, customerLocation.lng],
    ]);
  };

  return (
    <CustomModalComponent isOpen onRequestClose={modalStore.closeDeliveryTrackingModal} withCloseIcon modalMaxWidth={1033}>
      <ModalBackground className="delivery-tracking-modal__background">
        <ModalHeader
          title={loading ? '' : `${getTranslate(deliveryInfo?.takergo.dsp_name)}: `}
          wrapperClassName="align-items-center delivery-tracking-modal__header"
          titleClassName="text-size-24"
        >
          {loading ? (
            <>
              <Skeleton width={125} style={{ borderRadius: 6 }} height={30} />
              <Skeleton width={85} height={30} style={{ borderRadius: 6 }} className="ml-2" />
              <Skeleton width={140} height={30} style={{ borderRadius: 12 }} className="ml-4" />
              <Skeleton width={85} height={30} style={{ borderRadius: 12 }} className="ml-3" />
            </>
          ) : (
            <>
              <div className="family-medium text-size-24 ml-2 color-gray2">{truncateLongString(deliveryInfo?.takergo.reference_id)}</div>
              <CopyButton data={deliveryInfo?.takergo.reference_id} tooltipMessage="copied_to_clickboard" />
              <Label text={deliveryInfo?.takergo.dsp_status} color={getStatusColor(deliveryInfo?.takergo.taker_go_status)} className="ml-4" />
              <DueTime
                dueTime={getTimerDueTime(deliveryInfo?.takergo.last_resend_at_utc || deliveryInfo?.takergo.created_at_utc)}
                lastStatusDate={deliveryInfo?.takergo_details.slice(-1)[0].created_at_utc}
                stop={getTimerStopValue(deliveryInfo?.takergo.taker_go_status)}
              />
            </>
          )}
        </ModalHeader>
        <div className="delivery-tracking-modal__content">
          <div className="d-flex flex-1 flex-column height">
            <ScrollableBlock showScrollbar scrollbarSize="small">
              <TimeLine<TTakerGoDetails>
                trailing={renderTimeLineTrailing}
                data={deliveryInfo?.takergo_details}
                loading={loading}
                notFull={isNotFullTimeLine(deliveryInfo?.takergo.taker_go_status)}
              />
            </ScrollableBlock>
          </div>
          <DeliveryTrackingMapContainer onMapLoad={onMapLoad}>
            {!loading && deliveryInfo ? renderDriverLocation(deliveryInfo.takergo) : null}
            <Marker
              position={{ lat: branchLocation.lat, lng: branchLocation.lng }}
              icon={{ url: mapIcons.selectedBranch, scaledSize: new google.maps.Size(38, 38) }}
            />
            <Marker
              position={{ lat: customerLocation.lat, lng: customerLocation.lng }}
              icon={{ url: mapIcons.pin, scaledSize: new google.maps.Size(32, 32) }}
            />
          </DeliveryTrackingMapContainer>
        </div>
      </ModalBackground>
    </CustomModalComponent>
  );
});

export default DeliveryTrackingModal;
